import PropTypes from 'prop-types';
import {createRef, useEffect, useState} from 'react';
import {H2} from '@/typography';
import {Row, Col} from '@/grid';
import Dropdown from '@/form-fields/dropdown';
import Money from '@/ui/money';
import cx from 'classnames';
import Link from '@/links';
import MobileAccordion from '@/ui/mobile-accordion';
import {useTranslations} from 'next-intl';

// Styles
import {
  YEAR_MONTHS,
  THOUSAND_PLUS,
  PLAN_YEARLY,
  PLAN_MONTHLY,
  PLAN_BUNDLE,
  PLAN_HR,
  PLAN_RECRUITING
} from '@/content/pricing/plans-section/plan.const';
import styles from './styles.module.scss';

import {
  PlanBillingCycle,
  PlanFreeFeatures,
  PlanHRBox,
  PlanRecruitingBox,
  PlanServices,
  getPeriodPrice
} from './plan-boxes';

const propTypes = {
  plan: PropTypes.object.isRequired,
  variant: PropTypes.string,
  companySize: PropTypes.string,
  onCompanySizeChange: PropTypes.func.isRequired,
  isHREnabled: PropTypes.bool.isRequired,
  isRecruitingEnabled: PropTypes.bool.isRequired,
  setIsHREnabled: PropTypes.func.isRequired,
  setIsRecruitingEnabled: PropTypes.func.isRequired
};

const Plan = ({
  plan = {},
  variant = '',
  companySize,
  onCompanySizeChange,
  isHREnabled,
  isRecruitingEnabled,
  setIsHREnabled,
  setIsRecruitingEnabled
}) => {
  const t = useTranslations('pricing.plansSection.plan');
  const pricesKeys = Object.keys(plan.prices);
  const [billingCycle, setBillingCycle] = useState(plan.monthly_only ? PLAN_MONTHLY : PLAN_YEARLY);
  const currency = 'USD';

  const dropdownRef = createRef();
  const hrToggleRef = createRef();
  const recruitingToggleRef = createRef();

  const hideDiscountPrices = companySize !== THOUSAND_PLUS || pricesKeys.length === 1;

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.selectItemByValue(companySize);
      dropdownRef.current.clickAnimation();
    }
  }, [companySize]);

  useEffect(() => {
    if (!isHREnabled && !isRecruitingEnabled) setIsHREnabled(true);

    if (recruitingToggleRef.current) {
      recruitingToggleRef.current.toggleState(isRecruitingEnabled, true);
    }
  }, [isRecruitingEnabled]);

  useEffect(() => {
    if (!isHREnabled && !isRecruitingEnabled) setIsRecruitingEnabled(true);

    if (hrToggleRef.current) hrToggleRef.current.toggleState(isHREnabled, true);
  }, [isHREnabled]);

  const isPlanRecruitingEnabled = () => !plan?.recruiting_enabled || isRecruitingEnabled;
  const isPlaHREnabled = () => !plan?.hr_enabled || isHREnabled;

  const getPlanBundleType = () => {
    if (isPlaHREnabled() && isPlanRecruitingEnabled()) return PLAN_BUNDLE;

    if (isPlaHREnabled()) return PLAN_HR;

    if (isPlanRecruitingEnabled()) return PLAN_RECRUITING;
  };

  const getPrice = () => {
    const planBundle = getPlanBundleType();

    return pricesKeys.length === 1
      ? getPeriodPrice(plan.prices[pricesKeys[0]][planBundle], billingCycle)
      : getPeriodPrice(plan.prices[companySize][planBundle], billingCycle);
  };

  const getYearlyTotalPrice = () => getPrice() * YEAR_MONTHS;

  const getCompanySizeDropdown = () => {
    if (pricesKeys.length === 1) {
      return <div className={styles['plan__dropdown-skeleton']}>{pricesKeys[0]}</div>;
    }

    const companySizeValues = Object.keys(plan.prices).map(teamSize => ({
      text: (
        <>
          {teamSize}
          {` ${t('employees')}`}
        </>
      ),
      value: teamSize
    }));

    return (
      <Dropdown
        ref={dropdownRef}
        name="company_size"
        placeholder={t('companySizePlaceholder')}
        defaultValue={companySize}
        values={companySizeValues}
        onChange={onCompanySizeChange}
      />
    );
  };

  const getTotalPrice = () => {
    if (hideDiscountPrices) {
      return (
        <div className={styles.plan__price}>
          <Money
            className={styles['plan__price-money']}
            currency={currency}
            value={getPrice()}
            animate
            key={getPrice()}
          />
          <span className={styles['plan__price-right']}>
            <em>{`/${t('month')}`}</em>
          </span>
        </div>
      );
    }

    return (
      <div className={styles['plan__contact-us']}>
        <Link variant="cta-default-xs" href="/demo">
          {t('contactUsForQuote')}
        </Link>
      </div>
    );
  };

  const getYearlyTotalPriceElement = () => {
    if (!hideDiscountPrices || plan?.monthly_only) return;

    return (
      <div className={cx(styles.plan__price, styles['plan__price--total'])}>
        <Money
          className={styles['plan__price-money-total']}
          currency={currency}
          value={getYearlyTotalPrice()}
          key={getYearlyTotalPrice()}
          animate
        />
        <span className={styles['plan__price-right']}>{`/${t('year')}`}</span>
      </div>
    );
  };

  return (
    <div
      className={cx(styles.plan, {
        [styles[`plan--${variant}`]]: variant
      })}>
      <div className={styles.plan__top}>
        <H2 className={styles.plan__name}>{plan.name}</H2>
        <div className={styles.plan__bundle}>
          <p>{t('bundleBanner', {discount: plan.bundleDiscount}).toUpperCase()}</p>
        </div>
        <div className={styles['plan__price-wrapper']}>
          <Row middle="xs">
            <Col xs={7}>{getTotalPrice()}</Col>
            <Col xs={5}>{getYearlyTotalPriceElement()}</Col>
          </Row>
        </div>

        <PlanBillingCycle {...{setBillingCycle, plan, companySize}} />

        {getCompanySizeDropdown()}
      </div>

      <MobileAccordion
        openTitle={t('showDetails')}
        closeTitle={t('hideDetails')}
        className={styles.plan__details}
        contentClassName={styles['plan__details-content']}>
        <PlanRecruitingBox
          {...{
            plan,
            isRecruitingEnabled,
            setIsRecruitingEnabled,
            isHREnabled,
            companySize,
            currency,
            billingCycle,
            recruitingToggleRef
          }}
        />

        <PlanHRBox
          {...{
            plan,
            isRecruitingEnabled,
            setIsHREnabled,
            isHREnabled,
            hrToggleRef,
            companySize,
            currency,
            billingCycle
          }}
        />

        <PlanFreeFeatures plan={plan} />

        <PlanServices plan={plan} />
      </MobileAccordion>
    </div>
  );
};

Plan.propTypes = propTypes;

export default Plan;
